import { useState, useEffect, useMemo } from "react";
import { useStore } from "zustand";
import { useGlobalStore } from "../../../state/StoreContext";
import { useProjectSelector } from "./useProjectSelector";
import { getStripePlanConfigKey, isSeatAddonPlan } from "@functions/plans/utilities";
import { getWorkspacePlanLimits } from "../../../clientServices/workspaceLimits";
import { getPendingWorkspaceInvites } from "../../../backendapi/workspaceApi";

import membersUtils from "@rflocal/shared/workspace/members";

export interface SeatAddonInfo {
    hasSeatAddon: boolean;
    totalOccupants: number;
    baseSeats: number;
    extraSeatsNeeded: number;
    maxAddonSeats: number;
    defaultSeatsToAdd: number;
    exceedsMax: boolean;
    pricePerSeat: number;
    seatPriceId: string | null;
    seatAnnualPriceId: string | null;
    isLoading: boolean;
}

export function useSeatAddonInfo(planKey: string): SeatAddonInfo {
    const globalStore = useGlobalStore();
    const workspace = useStore(globalStore, (state) => state.getWorkspace());
    const usage = useStore(globalStore, (state) => state.getUsage());
    const projectSelector = useProjectSelector();
    const [pendingInvites, setPendingInvites] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const workspaceId = workspace?.id;
    const members = workspace?.members;

    useEffect(() => {
        if (!workspaceId) {
            setIsLoading(false);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        getPendingWorkspaceInvites(workspaceId)
            .then((invites: any[]) => {
                if (!cancelled) {
                    setPendingInvites(invites || []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPendingInvites([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [workspaceId]);

    return useMemo(() => {
        const hasSeatAddon = isSeatAddonPlan(planKey);

        const countingMembers = membersUtils.getMembersCountingTowardUsageLimitMap(members || {});
        const memberCount = Object.keys(countingMembers).length;

        const nonRfInvites = (pendingInvites || []).filter(
            (invite: any) => !invite?.email?.endsWith("@roboflow.com")
        );
        const totalOccupants = memberCount + nonRfInvites.length;

        const planLimits = getWorkspacePlanLimits(planKey, true);
        const rawBaseSeats = Number(planLimits?.collaborators);
        const baseSeats = Number.isFinite(rawBaseSeats) ? rawBaseSeats : 0;
        const rawCurrentPlanSeats =
            usage?.plan === planKey ? Number(usage?.limit?.collaborators) : NaN;
        const currentPlanSeats = Number.isFinite(rawCurrentPlanSeats)
            ? rawCurrentPlanSeats
            : baseSeats;
        const effectiveBaseSeats = usage?.plan === planKey ? currentPlanSeats : baseSeats;

        const configKey = getStripePlanConfigKey(planKey);
        const planConfig = configKey ? projectSelector?.stripe()?.[configKey] : null;
        const seatConfig = planConfig?.customExpansion?.adminCollaborators;

        const maxAddonSeats = typeof seatConfig?.max === "number" ? seatConfig.max : 0;
        const extraSeatsNeeded = Math.max(0, totalOccupants - effectiveBaseSeats);
        const defaultSeatsToAdd = Math.min(extraSeatsNeeded, maxAddonSeats);
        const exceedsMax = extraSeatsNeeded > maxAddonSeats;

        return {
            hasSeatAddon,
            totalOccupants,
            baseSeats: effectiveBaseSeats,
            extraSeatsNeeded,
            maxAddonSeats,
            defaultSeatsToAdd,
            exceedsMax,
            pricePerSeat: seatConfig?.pricePerUnit ?? 0,
            seatPriceId: seatConfig?.priceId ?? null,
            seatAnnualPriceId: seatConfig?.annualPriceId ?? seatConfig?.upsellPriceId ?? null,
            isLoading
        };
    }, [
        planKey,
        members,
        pendingInvites,
        isLoading,
        projectSelector,
        usage?.plan,
        usage?.limit?.collaborators
    ]);
}
